import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Logo, parseDemoLogins, type DemoLogin } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';

export function Login() {
  const { t } = useTranslation();
  const { recoveryError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function forgot() {
    if (!email) { setError(t('auth.enterEmailFirst')); return; }
    setBusy(true); setError(null); setNotice(null);
    await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
    setBusy(false);
    setNotice(t('auth.resetSent'));
  }
  // Deliberately NOT exposing org_admin/superadmin via a one-click button (they can
  // manage users and org settings). Manager + Finance cover the demo.
  const demos = parseDemoLogins(import.meta.env.VITE_DEMO_LOGINS as string | undefined, ['manager', 'finance']);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(error.message);
    setBusy(false);
  }

  async function quick(d: DemoLogin) {
    setBusy(true);
    setError(null);
    const { error } = await supabase.auth.signInWithPassword({ email: d.email, password: d.password });
    if (error) setError(error.message);
    setBusy(false);
  }

  const field = 'h-10 w-full rounded-md border border-border bg-[#F7F8FA] px-3 text-fg outline-none focus-visible:shadow-focus';

  return (
    <div className="grid min-h-screen place-items-center bg-bg px-4">
      <div className="w-full max-w-[380px] rounded-card bg-card p-7 shadow-card">
        <div className="mb-6">
          <Logo variant="light" size={30} />
        </div>
        <h1 className="mb-5 text-lg font-semibold">{t('auth.signIn')}</h1>
        {recoveryError && <div className="mb-3 rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{t('auth.linkInvalid')} {recoveryError}</div>}
        {notice && <div className="mb-3 rounded-md bg-st-done-bg px-3 py-2 text-sm text-st-done-fg">{notice}</div>}
        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-muted">{t('auth.email')}</span>
            <input className={field} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-muted">{t('auth.password')}</span>
            <input className={field} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </label>
          {error && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{error}</div>}
          <Button type="submit" disabled={busy} className="mt-1 w-full">
            {busy ? t('auth.signingIn') : t('auth.signIn')}
          </Button>
          <button type="button" onClick={forgot} disabled={busy} className="mt-1 self-start text-sm text-accent hover:underline disabled:opacity-40">
            {t('auth.forgotPassword')}
          </button>
        </form>
        {demos.length > 0 && (
          <div className="mt-4 border-t border-border pt-4">
            <div className="mb-2 text-xs text-muted">{t('auth.demoAs')}</div>
            <div className="flex flex-wrap gap-2">
              {demos.map((d) => (
                <button key={d.role} type="button" disabled={busy} onClick={() => quick(d)}
                  className="rounded-md border border-border bg-[#F7F8FA] px-3 py-1.5 text-sm font-medium hover:bg-bg disabled:opacity-40">
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
